import { formatAddress } from '@medplum/core'
import type { Participant } from '@seen/core/models/patients/participant'
import { Extension } from '@tiptap/react'
import { format, parseISO } from 'date-fns'

interface TemplateVariableOptions {
  participant?: Participant
}

interface Replacement {
  from: number
  to: number
  text: string
}

interface VariableResolversLookup {
  [key: string]: (participant: Participant) => string
}

// TODO: improve discoverability of template variables when authoring templates
// TODO: work on control inversion for this pattern to enable the callers to specify contextually relevant variables and resolvers
const variableResolvers: VariableResolversLookup = {
  firstName: (participant) => participant.firstNameOfficialOrFallback ?? '',
  lastName: (participant) => participant.lastNameOfficialOrFallback ?? '',
  mrn: (participant) => participant.seenMRN ?? '',
  dob: (participant) =>
    participant.birthDate
      ? format(parseISO(participant.birthDate), 'MM/dd/yyyy')
      : '',
  enrollmentDate: (participant) =>
    participant.enrollmentDate
      ? format(participant.enrollmentDate, 'MM/dd/yyyy')
      : '',
  gender: (participant) => participant.genderInitial ?? '',
  email: (participant) => participant.primaryEmail ?? '',
  phone: (participant) => participant.primaryPhoneNumber ?? '',
  address: (participant) => {
    const address = participant.primaryAddress
    if (!address) {
      return ''
    }
    return formatAddress(address)
  },
}

const getTemplateVariableValue = (
  variable: string,
  participant?: Participant,
  originalMatch?: string
): string => {
  if (!participant || !originalMatch) {
    return originalMatch ?? variable
  }

  return variableResolvers[variable]?.(participant) ?? originalMatch
}

export const TemplateVariableNode = Extension.create<TemplateVariableOptions>({
  name: 'templateVariable',

  onCreate() {
    const { participant } = this.options
    if (!participant) {
      return
    }

    const { doc, tr } = this.editor.state
    let transaction = tr
    const replacements: Replacement[] = []

    doc.descendants((node, pos) => {
      if (typeof node.text === 'string') {
        const regex = /{{(\w+)}}/g
        let match: RegExpExecArray | null = null
        // need to assign to match
        while ((match = regex.exec(node.text)) !== null) {
          const [fullMatch, variable] = match
          const transformed = getTemplateVariableValue(
            variable,
            participant,
            fullMatch
          )
          if (transformed !== fullMatch) {
            replacements.push({
              from: pos + match.index,
              to: pos + match.index + fullMatch.length,
              text: transformed,
            })
          }
        }
      }
    })

    // Apply replacements in reverse order to maintain correct positions
    for (const { from, to, text } of replacements.reverse()) {
      transaction = transaction.insertText(text, from, to)
    }

    if (transaction.docChanged) {
      this.editor.view.dispatch(transaction)
    }
  },

  onUpdate() {
    const { participant } = this.options
    if (!participant) {
      return
    }

    const { doc, tr } = this.editor.state
    let transaction = tr
    const replacements: Replacement[] = []

    doc.descendants((node, pos) => {
      if (typeof node.text === 'string') {
        const regex = /{{(\w+)}}/g
        let match: RegExpExecArray | null = null
        // need to assign to match
        while ((match = regex.exec(node.text)) !== null) {
          const [fullMatch, variable] = match
          const transformed = getTemplateVariableValue(
            variable,
            participant,
            fullMatch
          )
          if (transformed !== fullMatch) {
            replacements.push({
              from: pos + match.index,
              to: pos + match.index + fullMatch.length,
              text: transformed,
            })
          }
        }
      }
    })

    // Apply replacements in reverse order to maintain correct positions
    for (const { from, to, text } of replacements.reverse()) {
      transaction = transaction.insertText(text, from, to)
    }

    if (transaction.docChanged) {
      this.editor.view.dispatch(transaction)
    }
  },

  addInputRules() {
    const participant = this.options.participant

    return [
      {
        find: /{{(\w+)}}/g,
        undoable: true,
        handler: ({ state, match, range }) => {
          if (!participant) {
            return
          }

          const [fullMatch, variable] = match
          const transformed = getTemplateVariableValue(
            variable,
            participant,
            fullMatch
          )
          state.tr.insertText(transformed, range.from, range.to)
        },
      },
    ]
  },
})
